import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import PageSkeleton from '../../components/dashboard/PageSkeleton';
import BotAvatar from '../../components/BotAvatar';
import {
  Loader2, Save, CheckCircle2, AlertCircle, ToggleLeft, ToggleRight,
  Trophy, Star, X, Plus, Trash2, MessageSquare, Zap, Crown,
  ArrowUpCircle, Shield, Sparkles
} from 'lucide-react';

interface LevelingConfigProps {
  guildId: string;
  guildData: any;
}

interface LevelRole {
  level: number;
  role_id: string;
}

interface MultiplierRole {
  role_id: string;
  multiplier: number;
}

interface LevelingState {
  enabled: boolean;
  xp_per_message: { min: number; max: number };
  xp_cooldown: number;
  level_up_channel: string;
  level_up_message: string;
  stack_roles: boolean;
  ignored_channels: string[];
  ignored_roles: string[];
  no_xp_roles: string[];
  multiplier_roles: MultiplierRole[];
  level_roles: LevelRole[];
  embed_color: string;
  show_level_up_image: boolean;
}

const DEFAULT_STATE: LevelingState = {
  enabled: false,
  xp_per_message: { min: 15, max: 25 },
  xp_cooldown: 60,
  level_up_channel: 'current',
  level_up_message: '🌸 **{user}** just reached **Level {level}**! Keep chatting!',
  stack_roles: true,
  ignored_channels: [],
  ignored_roles: [],
  no_xp_roles: [],
  multiplier_roles: [],
  level_roles: [],
  embed_color: '#FF94B8',
  show_level_up_image: true,
};

function Toggle({ enabled, onChange }: { enabled: boolean; onChange: (v: boolean) => void }) {
  return (
    <button type="button" onClick={() => onChange(!enabled)} className="transition-colors shrink-0">
      {enabled ? <ToggleRight className="w-7 h-7 text-primary" /> : <ToggleLeft className="w-7 h-7 text-gray-700" />}
    </button>
  );
}

function SectionCard({
  icon: Icon, title, description, children, collapsible = false, defaultOpen = true,
}: {
  icon: any; title: string; description: string; children?: React.ReactNode; collapsible?: boolean; defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border rounded-xl overflow-hidden bg-dark-800/40 border-white/[0.06]">
      <button
        type="button"
        onClick={collapsible ? () => setOpen(!open) : undefined}
        className={`w-full flex items-center justify-between px-5 py-4 ${collapsible ? 'cursor-pointer hover:bg-white/[0.02]' : 'cursor-default'}`}
      >
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-primary/10 text-primary">
            <Icon className="w-4 h-4" />
          </div>
          <div className="text-left">
            <p className="text-[14px] font-semibold text-white">{title}</p>
            <p className="text-gray-500 text-[12px]">{description}</p>
          </div>
        </div>
        {collapsible && (
          <span className={`text-gray-600 text-xs transition-transform ${open ? 'rotate-180' : ''}`}>▼</span>
        )}
      </button>
      {(!collapsible || open) && children && (
        <div className="px-5 pb-5 pt-1 border-t border-white/[0.04] space-y-4">
          {children}
        </div>
      )}
    </div>
  );
}

function ChannelSelect({
  value, onChange, channels, placeholder = 'Select channel', allowSpecial = false,
}: {
  value: string; onChange: (v: string) => void; channels: any[]; placeholder?: string; allowSpecial?: boolean;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-dark-700/60 border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white appearance-none focus:outline-none focus:border-primary/40"
    >
      {allowSpecial && (
        <>
          <option value="current">📍 Channel where they leveled up</option>
          <option value="none">🔇 Don't send level-up messages</option>
        </>
      )}
      {!allowSpecial && <option value="">{placeholder}</option>}
      {channels.map((c: any) => (
        <option key={c.id} value={c.id}>#{c.name}</option>
      ))}
    </select>
  );
}

function RoleSelect({
  value, onChange, roles, placeholder = 'Select role',
}: {
  value: string; onChange: (v: string) => void; roles: any[]; placeholder?: string;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-dark-700/60 border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white appearance-none focus:outline-none focus:border-primary/40"
    >
      <option value="">{placeholder}</option>
      {roles.map((r: any) => (
        <option key={r.id} value={r.id}>@{r.name}</option>
      ))}
    </select>
  );
}

function MultiSelect({
  selected, onChange, items, renderItem, placeholder,
}: {
  selected: string[]; onChange: (v: string[]) => void; items: any[]; renderItem: (item: any) => string; placeholder: string;
}) {
  const available = items.filter((i: any) => !selected.includes(i.id));
  return (
    <div className="space-y-2">
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selected.map((id) => {
            const item = items.find((i: any) => i.id === id);
            return (
              <span key={id} className="inline-flex items-center gap-1 bg-dark-700/80 text-gray-300 text-xs px-2.5 py-1 rounded-lg border border-white/[0.06]">
                {item ? renderItem(item) : id}
                <button onClick={() => onChange(selected.filter((s) => s !== id))} className="text-gray-600 hover:text-red-400 transition-colors">
                  <X className="w-3 h-3" />
                </button>
              </span>
            );
          })}
        </div>
      )}
      {available.length > 0 && (
        <select
          value=""
          onChange={(e) => { if (e.target.value) onChange([...selected, e.target.value]); }}
          className="w-full bg-dark-700/60 border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-gray-400 appearance-none focus:outline-none focus:border-primary/40"
        >
          <option value="">{placeholder}</option>
          {available.map((i: any) => (
            <option key={i.id} value={i.id}>{renderItem(i)}</option>
          ))}
        </select>
      )}
    </div>
  );
}

export default function LevelingConfig({ guildId, guildData }: LevelingConfigProps) {
  const { token } = useAuth();
  const [config, setConfig] = useState<LevelingState>(DEFAULT_STATE);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const channels = guildData?.channels || [];
  const roles = guildData?.roles || [];

  useEffect(() => {
    if (!guildId || !token) return;

    async function fetchConfig() {
      try {
        const API_BASE = import.meta.env.VITE_API_URL || '';
        const res = await fetch(`${API_BASE}/api/dashboard/${guildId}/leveling`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setConfig({
            enabled: data.enabled ?? false,
            xp_per_message: data.xp_per_message ?? { min: 15, max: 25 },
            xp_cooldown: data.xp_cooldown ?? 60,
            level_up_channel: data.level_up_channel ?? 'current',
            level_up_message: data.level_up_message ?? DEFAULT_STATE.level_up_message,
            stack_roles: data.stack_roles ?? true,
            ignored_channels: data.ignored_channels ?? [],
            ignored_roles: data.ignored_roles ?? [],
            no_xp_roles: data.no_xp_roles ?? [],
            multiplier_roles: data.multiplier_roles ?? [],
            level_roles: data.level_roles ?? [],
            embed_color: data.embed_color ?? '#FF94B8',
            show_level_up_image: data.show_level_up_image ?? true,
          });
        }
      } catch (err) {
        console.error('Failed to fetch leveling config:', err);
      } finally {
        setLoading(false);
      }
    }

    fetchConfig();
  }, [guildId, token]);

  async function handleSave() {
    if (!token) return;
    setSaving(true);
    setSaveStatus('idle');

    try {
      const API_BASE = import.meta.env.VITE_API_URL || '';
      const res = await fetch(`${API_BASE}/api/dashboard/${guildId}/leveling`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(config),
      });

      setSaveStatus(res.ok ? 'success' : 'error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } finally {
      setSaving(false);
    }
  }

  function updateConfig<K extends keyof LevelingState>(key: K, value: LevelingState[K]) {
    setConfig((prev) => ({ ...prev, [key]: value }));
  }

  function addLevelRole() {
    const nextLevel = config.level_roles.length > 0
      ? Math.max(...config.level_roles.map((r) => r.level)) + 5
      : 5;
    updateConfig('level_roles', [...config.level_roles, { level: nextLevel, role_id: '' }]);
  }

  function removeLevelRole(index: number) {
    updateConfig('level_roles', config.level_roles.filter((_, i) => i !== index));
  }

  function updateLevelRole(index: number, field: keyof LevelRole, value: any) {
    const updated = [...config.level_roles];
    updated[index] = { ...updated[index], [field]: value };
    updateConfig('level_roles', updated);
  }

  function addMultiplierRole() {
    updateConfig('multiplier_roles', [...config.multiplier_roles, { role_id: '', multiplier: 1.5 }]);
  }

  function removeMultiplierRole(index: number) {
    updateConfig('multiplier_roles', config.multiplier_roles.filter((_, i) => i !== index));
  }

  function updateMultiplierRole(index: number, field: keyof MultiplierRole, value: any) {
    const updated = [...config.multiplier_roles];
    updated[index] = { ...updated[index], [field]: value };
    updateConfig('multiplier_roles', updated);
  }

  if (loading || !guildData) {
    return <PageSkeleton rows={4} />;
  }

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Page Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-display font-bold text-white flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-primary" />
            Leveling System
          </h2>
          <p className="text-gray-400 text-sm mt-1">
            Reward members for chatting with XP, levels, and role rewards
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className={`inline-flex items-center gap-2 px-6 py-2.5 rounded-xl font-semibold text-sm transition-all active:scale-[0.97] ${
            saveStatus === 'success'
              ? 'bg-green-500/20 text-green-400 border border-green-500/30'
              : saveStatus === 'error'
              ? 'bg-red-500/20 text-red-400 border border-red-500/30'
              : 'bg-primary hover:bg-primary-dark text-white hover:shadow-lg hover:shadow-primary/25'
          }`}
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : saveStatus === 'success' ? <CheckCircle2 className="w-4 h-4" /> : saveStatus === 'error' ? <AlertCircle className="w-4 h-4" /> : <Save className="w-4 h-4" />}
          {saving ? 'Saving...' : saveStatus === 'success' ? 'Saved!' : saveStatus === 'error' ? 'Failed' : 'Save Changes'}
        </button>
      </div>

      {/* Master Toggle */}
      <div className={`border rounded-xl p-5 transition-all ${
        config.enabled ? 'bg-primary/5 border-primary/20' : 'bg-dark-800/40 border-white/[0.06]'
      }`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2.5 rounded-xl ${config.enabled ? 'bg-primary/20 text-primary' : 'bg-dark-700/60 text-gray-600'}`}>
              <Trophy className="w-5 h-5" />
            </div>
            <div>
              <p className="text-white font-semibold">Enable Leveling</p>
              <p className="text-gray-500 text-xs mt-0.5">Members earn XP for chatting and unlock roles at milestones</p>
            </div>
          </div>
          <Toggle enabled={config.enabled} onChange={(v) => updateConfig('enabled', v)} />
        </div>
      </div>

      {config.enabled && (
        <>
          {/* XP Settings */}
          <SectionCard icon={Zap} title="XP Settings" description="How much XP members earn per message">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs text-gray-400 mb-1.5">Min XP per message</label>
                <input
                  type="number" min={1} max={100}
                  value={config.xp_per_message.min}
                  onChange={(e) => updateConfig('xp_per_message', { ...config.xp_per_message, min: Number(e.target.value) || 1 })}
                  className="w-full bg-dark-700/60 border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-primary/40"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1.5">Max XP per message</label>
                <input
                  type="number" min={1} max={200}
                  value={config.xp_per_message.max}
                  onChange={(e) => updateConfig('xp_per_message', { ...config.xp_per_message, max: Number(e.target.value) || 25 })}
                  className="w-full bg-dark-700/60 border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-primary/40"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1.5">Cooldown (seconds)</label>
                <input
                  type="number" min={0} max={300}
                  value={config.xp_cooldown}
                  onChange={(e) => updateConfig('xp_cooldown', Number(e.target.value) || 60)}
                  className="w-full bg-dark-700/60 border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-primary/40"
                />
              </div>
            </div>
            <p className="text-gray-600 text-[11px]">Members earn between {config.xp_per_message.min}–{config.xp_per_message.max} XP per message, with a {config.xp_cooldown}s cooldown between gains.</p>
          </SectionCard>

          {/* Level-Up Announcements */}
          <SectionCard icon={MessageSquare} title="Level-Up Announcements" description="How and where level-ups are announced">
            <div>
              <label className="block text-xs text-gray-400 mb-1.5">Announcement Channel</label>
              <ChannelSelect
                value={config.level_up_channel}
                onChange={(v) => updateConfig('level_up_channel', v)}
                channels={channels}
                allowSpecial
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1.5">Level-Up Message</label>
              <textarea
                value={config.level_up_message}
                onChange={(e) => updateConfig('level_up_message', e.target.value)}
                rows={2}
                maxLength={500}
                className="w-full bg-dark-700/60 border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-primary/40 resize-none"
                placeholder="🌸 **{user}** just reached **Level {level}**!"
              />
              <p className="text-gray-600 text-[11px] mt-1">
                Variables: <code className="text-primary/60">{'{user}'}</code> <code className="text-primary/60">{'{level}'}</code> <code className="text-primary/60">{'{role}'}</code> (reward role, if any)
              </p>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-300">Show level-up image</p>
                <p className="text-gray-600 text-[11px]">Display a styled Anya-themed image with the level-up embed</p>
              </div>
              <Toggle enabled={config.show_level_up_image} onChange={(v) => updateConfig('show_level_up_image', v)} />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1.5">Embed Color</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={config.embed_color}
                  onChange={(e) => updateConfig('embed_color', e.target.value)}
                  className="w-8 h-8 rounded-lg border border-white/[0.06] cursor-pointer bg-transparent"
                />
                <input
                  type="text"
                  value={config.embed_color}
                  onChange={(e) => updateConfig('embed_color', e.target.value)}
                  className="w-24 bg-dark-700/60 border border-white/[0.06] rounded-lg px-3 py-1.5 text-xs text-white font-mono focus:outline-none focus:border-primary/40"
                />
              </div>
            </div>
          </SectionCard>

          {/* Level Roles */}
          <SectionCard icon={Crown} title="Level Roles" description="Automatically assign roles when members reach a level">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <p className="text-sm text-gray-300">Stack roles</p>
                <span className="text-gray-600 text-[11px]">(keep previous level roles)</span>
              </div>
              <Toggle enabled={config.stack_roles} onChange={(v) => updateConfig('stack_roles', v)} />
            </div>

            {config.level_roles.length > 0 && (
              <div className="space-y-2">
                {config.level_roles.map((lr, i) => (
                  <div key={i} className="flex items-center gap-2 bg-dark-700/40 rounded-lg p-2.5 border border-white/[0.04]">
                    <div className="shrink-0">
                      <label className="block text-[10px] text-gray-600 mb-0.5">Level</label>
                      <input
                        type="number" min={1} max={200}
                        value={lr.level}
                        onChange={(e) => updateLevelRole(i, 'level', Number(e.target.value) || 1)}
                        className="w-16 bg-dark-800/60 border border-white/[0.06] rounded-md px-2 py-1.5 text-sm text-white text-center focus:outline-none focus:border-primary/40"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <label className="block text-[10px] text-gray-600 mb-0.5">Role reward</label>
                      <RoleSelect
                        value={lr.role_id}
                        onChange={(v) => updateLevelRole(i, 'role_id', v)}
                        roles={roles}
                        placeholder="Choose a role..."
                      />
                    </div>
                    <button
                      onClick={() => removeLevelRole(i)}
                      className="shrink-0 mt-4 p-1.5 text-gray-600 hover:text-red-400 transition-colors rounded-md hover:bg-red-500/10"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <button
              onClick={addLevelRole}
              className="inline-flex items-center gap-1.5 text-xs text-primary hover:text-primary-light transition-colors font-medium"
            >
              <Plus className="w-3.5 h-3.5" /> Add level role
            </button>

            {config.level_roles.length === 0 && (
              <div className="text-center py-6 text-gray-600">
                <Crown className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No level roles configured yet</p>
                <p className="text-xs mt-0.5">Add roles to automatically reward members when they reach a level</p>
              </div>
            )}
          </SectionCard>

          {/* XP Multipliers */}
          <SectionCard icon={Star} title="XP Multipliers" description="Roles that earn bonus XP" collapsible defaultOpen={false}>
            {config.multiplier_roles.length > 0 && (
              <div className="space-y-2">
                {config.multiplier_roles.map((mr, i) => (
                  <div key={i} className="flex items-center gap-2 bg-dark-700/40 rounded-lg p-2.5 border border-white/[0.04]">
                    <div className="flex-1 min-w-0">
                      <label className="block text-[10px] text-gray-600 mb-0.5">Role</label>
                      <RoleSelect
                        value={mr.role_id}
                        onChange={(v) => updateMultiplierRole(i, 'role_id', v)}
                        roles={roles}
                        placeholder="Choose a role..."
                      />
                    </div>
                    <div className="shrink-0">
                      <label className="block text-[10px] text-gray-600 mb-0.5">Multiplier</label>
                      <input
                        type="number" min={0.1} max={10} step={0.1}
                        value={mr.multiplier}
                        onChange={(e) => updateMultiplierRole(i, 'multiplier', Number(e.target.value) || 1)}
                        className="w-20 bg-dark-800/60 border border-white/[0.06] rounded-md px-2 py-1.5 text-sm text-white text-center focus:outline-none focus:border-primary/40"
                      />
                    </div>
                    <button
                      onClick={() => removeMultiplierRole(i)}
                      className="shrink-0 mt-4 p-1.5 text-gray-600 hover:text-red-400 transition-colors rounded-md hover:bg-red-500/10"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <button
              onClick={addMultiplierRole}
              className="inline-flex items-center gap-1.5 text-xs text-primary hover:text-primary-light transition-colors font-medium"
            >
              <Plus className="w-3.5 h-3.5" /> Add multiplier role
            </button>
          </SectionCard>

          {/* Ignored Channels & Roles */}
          <SectionCard icon={Shield} title="Exclusions" description="Channels and roles that don't earn XP" collapsible defaultOpen={false}>
            <div>
              <label className="block text-xs text-gray-400 mb-1.5">Ignored Channels (no XP earned here)</label>
              <MultiSelect
                selected={config.ignored_channels}
                onChange={(v) => updateConfig('ignored_channels', v)}
                items={channels}
                renderItem={(c) => `#${c.name}`}
                placeholder="Add a channel..."
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1.5">No-XP Roles (members with these roles earn 0 XP)</label>
              <MultiSelect
                selected={config.no_xp_roles}
                onChange={(v) => updateConfig('no_xp_roles', v)}
                items={roles}
                renderItem={(r) => `@${r.name}`}
                placeholder="Add a role..."
              />
            </div>
          </SectionCard>

          {/* Preview */}
          <SectionCard icon={ArrowUpCircle} title="Preview" description="How a level-up message will look">
            <div className="bg-[#2b2d31] rounded-lg p-4 border border-[#3f4248]">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full overflow-hidden shrink-0">
                  <BotAvatar className="w-full h-full object-cover" size={40} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-primary font-semibold text-sm">Anya Bot</span>
                    <span className="text-xs text-gray-500">Today at {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                  </div>
                  <div
                    className="rounded-lg p-3 border-l-4"
                    style={{ borderColor: config.embed_color, backgroundColor: 'rgba(0,0,0,0.15)' }}
                  >
                    <p className="text-sm text-gray-200">
                      {config.level_up_message
                        .replace('{user}', '**@Example User**')
                        .replace('{level}', '**10**')
                        .replace('{role}', config.level_roles.length > 0 ? '**@Level 10 Role**' : '')
                        .split('**').map((part, i) =>
                          i % 2 === 1 ? <strong key={i} className="text-white">{part}</strong> : part
                        )}
                    </p>
                    {config.show_level_up_image && (
                      <div className="mt-2 bg-gradient-to-r from-primary/20 to-pink-500/10 rounded-md p-3 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full overflow-hidden shrink-0">
                          <BotAvatar className="w-full h-full object-cover" size={40} />
                        </div>
                        <div>
                          <p className="text-white font-bold text-sm">Level Up!</p>
                          <p className="text-primary text-xs">Level 9 → Level 10</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </SectionCard>
        </>
      )}
    </div>
  );
}
